/**
 * The few plain-data decisions the map's site detail panel makes before it can
 * render a site: which site group the site is being viewed under, and which
 * word names the site's operational state.
 *
 * The map reaches a site without a group in its URL, so the panel takes the
 * site's primary group from the snapshot and rebuilds the route params every
 * dashboard component expects to be handed.
 *
 * Returns plain JS — no JSX, no redux.
 */
import { SITE_STATUS } from '@sophocles/features/map/mapState';
/**
 * The route params the site dashboard's cards, blades and links are built from.
 *
 * The map knows the customer and the site; the group comes from the snapshot's
 * primary owner, so these are only complete once the site has loaded.
 *
 * @param {?object} site - the site snapshot, or null before it resolves.
 * @param {string|number} customerId - the customer whose map is open.
 * @param {string|number} siteId - the selected site.
 * @returns {?{cust_id: (string|number), sitegroup_id: (string|number), site_id: (string|number)}}
 *   the params, or null while the group is still unknown.
 */
export function buildSiteDetailParams(site, customerId, siteId) {
  const siteGroupId = site?.owner?.id ?? null;
  if (siteGroupId == null) return null;

  return { cust_id: customerId, sitegroup_id: siteGroupId, site_id: siteId };
}

/**
 * Which status the dot should be named by, for the same site state its colour
 * is taken from.
 *
 * The dashboard's status token collapses several situations onto one colour —
 * a site standing in a warning-severity alarm and a site that has simply gone
 * quiet both read amber — so the wording is decided from the underlying state
 * rather than from the token. The branch order is deliberately the same one
 * deriveSiteCardStatus takes, which is what keeps the word and the colour
 * describing the same thing.
 *
 * @param {object} state
 * @param {boolean} state.hasDevice - a unit is attached to the site.
 * @param {boolean} state.hasRadio - that unit has a radio.
 * @param {boolean} state.hasEverReported - the radio has checked in at least once.
 * @param {?boolean} state.isRadioOnline - the radio's current reachability.
 * @param {number} state.alarmCount - alarms the viewer is allowed to see.
 * @returns {string} the SITE_STATUS the dot should be named by.
 */
export function deriveSiteStatusWording({ hasDevice, hasRadio, hasEverReported, isRadioOnline, alarmCount }) {
  if (!hasDevice) return SITE_STATUS.SILENT;
  if (alarmCount > 0) return SITE_STATUS.ALARMING;
  if (!hasRadio || !hasEverReported) return SITE_STATUS.SILENT;
  return isRadioOnline === false ? SITE_STATUS.OFFLINE : SITE_STATUS.ONLINE;
}
